import { Product } from '../types';
import { DOCS_ORIGIN } from '../config/site';
import { Locale } from '../i18n/locales';
import { ProductsKm } from './products.km';

export const NAV_PRODUCT_SLUGS = [
  'crm',
  'revenues',
  'expenses',
  'inventory',
  'accounting',
  'fixed-assets',
  'hrms',
  'pos',
  'online-store',
  'clinic',
] as const;

export const Products: Product[] = [
  {
    id: 1,
    slug: 'hrms',
    name: 'Human Resource Management',
    shortName: 'HRMS',
    navLabel: 'HR Management',
    tagline: 'Hire, attend, pay, and develop people in one system',
    featured: true,
    docsUrl: `${DOCS_ORIGIN}/en/hrms/`,
    description:
      'AKAS HRMS is a full hire-to-retire suite. Employees request leave, overtime, training, job requisitions, and resignation from a self-service dashboard. HR runs recruitment, personnel, attendance, leave, performance, payroll (including salary tax, NSSF, and seniority), and training — with approval workflows, multilingual screens, and records that stay in one encrypted database.',
    images: [
      '/images/products/hr-db-1.png',
      '/images/products/hr-db-2.png',
      '/images/products/hr-employee-list.png',
      '/images/products/hr-employee-form.png',
      '/images/products/hr-employment.png',
      '/images/products/hr-employment-history.png',
      '/images/products/hr-leave-request.png',
      '/images/products/hr-mypayslip.png',
      '/images/products/hr-approval-workflow.png',
    ],
    backgroundImage: '/images/backgrounds/hrms.jpg',
    highlights: [
      'Employee self-service for leave, OT, payslips, training, and resignation',
      'Payroll with salary tax (TOS01), NSSF eForm, seniority, bank lists, and payslips',
      'Attendance from biometric devices, QR check-in, rosters, and timesheets',
      'Approval workflows with temporary approval delegation',
    ],
    workflow: {
      title: 'Hire to retire',
      path: 'Requisition → Hire → Attendance & leave → Payroll → Review → Separation',
      steps: [
        {
          title: 'Recruit',
          description:
            'Employees raise job requisitions and get approved. It becomes vacancies, collects applications, notifies applicants, and hires into Personnel.',
        },
        {
          title: 'Employ',
          description:
            'Create the person profile, employment contract, salary, leave quotas, work schedule, and reporting line.',
        },
        {
          title: 'Attend & leave',
          description:
            'Capture punches, missions, and QR check-ins, process timesheets, and approve leave against balances and holidays.',
        },
        {
          title: 'Pay & develop',
          description:
            'Process overtime, pay queues, and the payroll register. Run performance reviews and training with self-service.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Self-service dashboard',
        items: [
          'Leave request, leave cancel, my leaves, and leave calendar',
          'OT payment request and My Payslip',
          'Training request and performance review',
          'Job requisition, job vacancies, resignation request, and inventory withdrawal',
        ],
      },
      {
        title: 'Personnel',
        items: [
          'Employee administration: ID, email, reporting line, work unit, salary, leave quotas, schedule',
          'Employment contracts, rotation/promotion, misconduct, awards, and accidents',
          'Separation for resignation or termination, started from self-service when needed',
        ],
      },
      {
        title: 'Payroll with local statutory reports',
        items: [
          'Pay elements, pay policies, pay periods — including a half-period mode for twice-monthly payroll — OT types/policies, shift allowances, and child-care policies',
          'Computed pay elements with formula variables, salary tax brackets, rating tables, pay queues, and payroll register (calculate, review, approve) with payslips',
          'Statutory reports: PAY004 NSSF eForm, PAY005 Tax on Salary (TOS01), PAY006 Fringe Benefit tax, PAY009 Seniority Pay, PAY010 Pension Fund — plus PAY003/PAY008 bank transfer lists',
        ],
      },
    ],
    mainFeatures: [
      {
        icon: '💻',
        title: 'Online self-service',
        description:
          'After login, employees use Dashboard → Self Service for day-to-day requests: update profile, view schedule, request leave, claim overtime, start a performance review, request training, raise a job requisition, or submit resignation. Managers see pending approvals; employees see their own requests, payslips, vacancies, and messages. Approval Delegation lets someone temporarily hand off their approval authority.',
        images: [
          '/images/products/hr-ess-leaverequest.png',
          '/images/products/hr-ess-leavecancel.png',
          '/images/products/hr-ess-myleave.png',
          '/images/products/hr-ess-resignrequest.png',
          '/images/products/hr-ess-jobrequisition.png',
          '/images/products/hr-ess-mypendingrequest.png',
        ],
      },
      {
        icon: '👥',
        title: 'Recruitment',
        description:
          'Define notify actions, then raise a job requisition (HR or employee self-service). After approval, manage applications against that vacancy. Candidates browse public/internal vacancies and apply online. Applicant profiles keep CVs, contacts, and skills; HR can notify shortlisted or rejected applicants through configured workflows, including AI-assisted resume parsing.',
        images: [
          '/images/products/hr-recruit-jobtitle.png',
          '/images/products/hr-recruit-jobrequisition.png',
          '/images/products/hr-recruit-manage-applicants.png',
          '/images/products/hr-recruit-manage-applicants2.png',
          '/images/products/hr-recruit-notify-applicant.png',
          '/images/products/hr-recruit-notify-custom.png',
        ],
      },
      {
        icon: '👤',
        title: 'Personnel',
        description:
          'Manage the employee account: ID, email, reporting line, hiring date, work unit, salary, leave quotas, and work schedule. Workflows cover hiring, rotation and promotion, insurance offers, awards, misconduct, accidents, and termination. Print employment contracts from the employment record.',
        images: [
          '/images/products/hr-employee-list.png',
          '/images/products/hr-employee-form.png',
          '/images/products/hr-employment.png',
          '/images/products/hr-employment-history.png',
          '/images/products/hr-emp-printcontract.png',
        ],
      },
      {
        icon: '📅',
        title: 'Leave and attendance',
        description:
          'Leave covers public holidays, leave types, work shifts, leave records, balance calculation, calendar, and reports. Attendance covers policies (late/early/absent rules), biometric devices, QR locations, missions, raw punch records, work rosters, processing into timesheets, and abnormal-attendance reports.',
        images: [
          '/images/products/hr-leave-holiday.png',
          '/images/products/hr-leave-leavetype.png',
          '/images/products/hr-leave-workshift.png',
          '/images/products/hr-leave-records.png',
          '/images/products/hr-leave-balance.png',
          '/images/products/hr-leave-report.png',
        ],
      },
      {
        icon: '📊',
        title: 'Performance management',
        description:
          'Define KPIs and rating groups, then build weighted review templates with self and supervisor reviewers. Employees start reviews from self-service; HR and supervisors follow up, approve, and print. Entries can also be captured by HR offline when needed.',
        images: [
          '/images/products/hr-perf-kpi.png',
          '/images/products/hr-perf-template.png',
          '/images/products/hr-perf-review.png',
        ],
      },
      {
        icon: '💰',
        title: 'Payroll',
        description:
          'Set up pay elements, OT, salary taxes, pay policies, pay periods, shift allowances, and child-care policies. Employees submit OT for approval. HR enters variable amounts in pay queues, then calculates, reviews, and approves the payroll register — printing payslips and running tax, NSSF, seniority, and bank-list reports.',
        images: [
          '/images/products/hr-payroll-element.png',
          '/images/products/hr-payroll-otpolicy.png',
          '/images/products/hr-payroll-salarytax.png',
          '/images/products/hr-payroll-otpayrequest.png',
          '/images/products/hr-payroll-register.png',
          '/images/products/hr-payroll-tos.png',
          '/images/products/hr-payroll-nssf1.png',
          '/images/products/hr-payroll-nssf2.png',
          '/images/products/hr-mypayslip.png',
          '/images/products/hr-payroll-payslip.png',
        ],
      },
    ],
    meta: {
      title: 'AKAS HRMS | Personnel, attendance, leave, payroll and self-service',
      description:
        'AKAS HRMS covers recruitment, personnel, attendance, leave, performance, payroll (tax, NSSF, seniority), training, and employee self-service with approval workflows.',
      keywords:
        'HRMS, HRIS, payroll NSSF, salary tax, leave management, attendance, recruitment, employee self-service, AKAS',
    },
    additionalFeatures: [
      {
        icon: '✓',
        title: 'Approval workflow',
        description:
          'Design multi-step approvals for leave, OT, requisitions, and more. Delegate approval authority when someone is away.',
      },
      {
        icon: '🌐',
        title: 'Multilingual',
        description: 'English and Khmer are included. Add more languages from translations in System Administration.',
      },
      {
        icon: '📁',
        title: 'Document management',
        description: 'Keep contracts, CVs, and employee files with the personnel record.',
      },
      {
        icon: '⚙️',
        title: 'Form layout & custom fields',
        description: 'Add fields and rearrange screens without a custom build.',
      },
      {
        icon: '🔔',
        title: 'Notifications',
        description: 'Email and in-app notices for requests, approvals, and payroll events.',
      },
      {
        icon: '📝',
        title: 'Audit logging',
        description: 'Data logs capture who changed sensitive HR fields, and when.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'Less paperwork for HR',
        description:
          'Employees submit leave, OT, training, and resignation themselves. HR reviews exceptions instead of re-keying forms.',
      },
      {
        number: '02',
        title: 'Payroll that matches local rules',
        description:
          'Salary tax, NSSF, seniority, and bank lists are part of the register — not a separate spreadsheet after the fact.',
      },
      {
        number: '03',
        title: 'One employee record',
        description:
          'Hiring, contract, attendance, leave balances, reviews, and payslips sit on the same person — so rotations and exits stay consistent.',
      },
      {
        number: '04',
        title: 'Approvals you can prove',
        description:
          'Configurable workflows, delegation, and audit logs give you a trail for labor, tax, and internal-control reviews.',
      },
    ],
    videos: [
      {
        title: 'Flexible approval workflow',
        youtubeId: 'Rt-Zk9H0rNg',
        description: 'Design approval workflow in AKAS.',
      },
      {
        title: 'Resume parsing',
        youtubeId: '5-htzI5-TVQ',
        description: 'AI-assisted resume parsing in recruitment.',
      },
      {
        title: 'HRMS demo in Khmer',
        youtubeId: 'sOLBiaDt1IE',
        description: 'AKAS HRMS demo in Khmer.',
      },
      {
        title: 'Customization',
        youtubeId: 'RlB4MXPjYXs',
        description: 'Form layout and customization demo.',
      },
    ],
    related: [
      { label: 'Platform & workflows', to: '/products/platform' },
      { label: 'Accounting', to: '/products/accounting' },
      { label: 'HRMS user guide', href: `${DOCS_ORIGIN}/en/hrms/` },
    ],
  },
  {
    id: 2,
    slug: 'crm',
    name: 'Customer Relationship Management',
    shortName: 'CRM',
    navLabel: 'CRM',
    tagline: 'From first contact to a priced quotation',
    featured: true,
    docsUrl: `${DOCS_ORIGIN}/en/crm/`,
    description:
      'AKAS CRM runs the sales pipeline from lead to quotation. Capture a prospect, convert them to a customer, track deal value and probability, send a formal quote, then continue in Revenues with sales orders, invoices, and receipts. Customer master data is shared with billing so sales and finance are looking at the same account.',
    images: [
      '/images/products/crm/crm-leads.png',
      '/images/products/crm/crm-lead-form.png',
      '/images/products/crm/crm-customers.png',
      '/images/products/crm/crm-customer-form.png',
      '/images/products/crm/crm-opportunities.png',
      '/images/products/crm/crm-opportunity-form.png',
      '/images/products/crm/crm-pipeline.png',
      '/images/products/crm/crm-quotations.png',
      '/images/products/crm/crm-quotation-form.png',
      '/images/products/crm/crm-customer-sales-history.png',
      '/images/products/crm/crm-reports.png',
    ],
    backgroundImage: '/images/products/crm/crm-pipeline.png',
    highlights: [
      'Leads convert to customers and optional opportunities',
      'Pipeline stages with deal value and probability',
      'Quotations approve and convert to sales orders',
      'Customer sales history and CRM reports',
    ],
    workflow: {
      title: 'Lead to quote',
      path: 'Leads → Customers → Opportunities → Quotations → Revenues',
      steps: [
        {
          title: 'Capture the lead',
          description: 'Record contact details and source so follow-up is not lost in inboxes.',
        },
        {
          title: 'Convert to a customer',
          description: 'Create the customer account used across CRM and Revenues, optionally with an opportunity.',
        },
        {
          title: 'Work the opportunity',
          description: 'Track value, stage, and probability on a pipeline the team can actually manage.',
        },
        {
          title: 'Quote, then bill',
          description: 'Send a formal quotation. After approval, convert it to a sales order in Revenues.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Pipeline pages',
        items: [
          'Leads, customers, opportunities, quotations, and pipeline view',
          'Customer sales history across orders and invoices',
          'CRM reports for activity and conversion',
        ],
      },
      {
        title: 'Connected to billing',
        items: [
          'Customer master is shared with Revenues',
          'Approved quotations become sales orders, then invoices and receipts',
          'Credit terms from System Administration apply to sales documents',
        ],
      },
    ],
    mainFeatures: [
      {
        icon: '👤',
        title: 'Leads and customers',
        description:
          'Capture prospects, convert them to customers, and keep one account for sales history, quotations, invoices, and receipts. Attach notes and files so the next person to call has context.',
        images: [
          '/images/products/crm/crm-leads.png',
          '/images/products/crm/crm-lead-form.png',
          '/images/products/crm/crm-customers.png',
          '/images/products/crm/crm-customer-form.png',
        ],
      },
      {
        icon: '📊',
        title: 'Opportunities and pipeline',
        description:
          'Track deal value, stage, and probability. The pipeline view shows where revenue is sitting so managers can coach the right deals, not just look at last month\'s invoices.',
        images: [
          '/images/products/crm/crm-opportunities.png',
          '/images/products/crm/crm-opportunity-form.png',
          '/images/products/crm/crm-pipeline.png',
        ],
      },
      {
        icon: '📝',
        title: 'Quotations',
        description:
          'Build an itemized quote with taxes, discounts, and payment terms. Route it for approval, send it to the customer, then convert it to a sales order without retyping lines.',
        images: [
          '/images/products/crm/crm-quotations.png',
          '/images/products/crm/crm-quotation-form.png',
        ],
      },
      {
        icon: '📈',
        title: 'Sales history and reports',
        description:
          'See what a customer has bought, which quotes converted, and how the pipeline is moving — using the same items and taxes as Revenues.',
        images: [
          '/images/products/crm/crm-customer-sales-history.png',
          '/images/products/crm/crm-reports.png',
        ],
      },
    ],
    meta: {
      title: 'AKAS CRM | Leads, pipeline, quotations and customer history',
      description:
        'AKAS CRM manages leads, customers, opportunities, quotations, and pipeline reporting, then hands off to sales orders and invoicing in Revenues.',
      keywords:
        'CRM, sales pipeline, quotations, leads, customer management, AKAS',
    },
    additionalFeatures: [
      {
        icon: '🌐',
        title: 'Multilingual',
        description: 'Run sales in the language your team actually uses.',
      },
      {
        icon: '✓',
        title: 'Quote approvals',
        description: 'Route discounts and large quotes through workflow before they become orders.',
      },
      {
        icon: '🔔',
        title: 'Notifications',
        description: 'Alert owners when a lead is assigned or a quotation needs approval.',
      },
      {
        icon: '🔌',
        title: 'Shared masters',
        description: 'Items, taxes, credit terms, and customers are reused in Revenues, POS, and the online store.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'No retyping from quote to invoice',
        description:
          'Approved quotations become sales orders. Sales and finance stop maintaining two versions of the same deal.',
      },
      {
        number: '02',
        title: 'Pipeline you can forecast',
        description:
          'Stage, value, and probability live on the opportunity — not in a personal spreadsheet.',
      },
      {
        number: '03',
        title: 'One customer record',
        description:
          'CRM, invoicing, receipts, and deposits all point at the same customer account.',
      },
      {
        number: '04',
        title: 'Handoff that does not drop',
        description:
          'When a quote is won, Revenues already has the customer, items, taxes, and terms.',
      },
    ],
    related: [
      { label: 'Revenues', to: '/products/revenues' },
      { label: 'Point of Sale', to: '/products/pos' },
      { label: 'CRM user guide', href: `${DOCS_ORIGIN}/en/crm/` },
    ],
  },
  {
    id: 3,
    slug: 'insurance',
    name: 'Insurance Management',
    shortName: 'Insurance',
    tagline: 'Policies, clients, claims, and renewals',
    featured: false,
    description:
      'AKAS Insurance Management helps brokers and insurers keep policy details, client history, claims, and renewals in one place — with reminders, document storage, and reporting.',
    images: ['/images/products/insurance.jpeg'],
    backgroundImage: '/images/products/insurance.jpeg',
    highlights: [
      'Policy register with coverage and renewal dates',
      'Client history and communication log',
      'Claims from submission to resolution',
      'Renewal reminders and quotes',
    ],
    mainFeatures: [
      {
        icon: '📊',
        title: 'Policy management',
        description:
          'Track policies, coverage, and renewal dates. Search, filter, and report on the portfolio instead of hunting through files.',
      },
      {
        icon: '👥',
        title: 'Client management',
        description:
          'Keep personal details, contact data, policy history, and communication logs on each client.',
      },
      {
        icon: '📝',
        title: 'Claims processing',
        description:
          'Move a claim from submission to resolution with documentation, status, and notifications.',
      },
      {
        icon: '📅',
        title: 'Renewal management',
        description:
          'Surface upcoming renewals, remind clients and agents, and generate renewal quotes.',
      },
    ],
    meta: {
      title: 'AKAS Insurance Management | Policies, claims and renewals',
      description:
        'Manage insurance policies, clients, claims, and renewals with reminders, documents, and reporting in AKAS.',
      keywords: 'Insurance management, policy, claims, renewal, AKAS',
    },
    additionalFeatures: [
      {
        icon: '💰',
        title: 'Premium calculation',
        description: 'Calculate premiums from configurable rating factors.',
      },
      {
        icon: '🔒',
        title: 'Document storage',
        description: 'Store policy files, claims packs, and client correspondence.',
      },
      {
        icon: '📨',
        title: 'Automated communications',
        description: 'Email and SMS for renewals, premiums, and claim updates.',
      },
      {
        icon: '⚙️',
        title: 'Custom workflows',
        description: 'Different paths for policy types, client categories, and claims.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'Faster operations',
        description: 'Policies, claims, and renewals stop living in disconnected folders.',
      },
      {
        number: '02',
        title: 'Better client service',
        description: 'Answer with policy details and history in front of you.',
      },
      {
        number: '03',
        title: 'Renewals that are not missed',
        description: 'Dates and reminders sit on the policy, not in a calendar nobody shares.',
      },
      {
        number: '04',
        title: 'A trail for reviews',
        description: 'Documents and status history stay with the policy and the claim.',
      },
    ],
  },
  {
    id: 4,
    slug: 'revenues',
    name: 'Revenues',
    shortName: 'Revenues',
    navLabel: 'Revenues',
    tagline: 'Quote to cash: orders, invoices, receipts, and AR',
    featured: true,
    docsUrl: `${DOCS_ORIGIN}/en/revenue/`,
    description:
      'Revenues is the sales-to-cash cycle in AKAS. After CRM quotations, you confirm sales orders, bill with invoices, record receipts, take customer deposits, and watch receivables. Products and services, price levels, VAT, coupons, and KHQR payment acceptance are set up once and reused by POS and the online store. Approved invoices can post to the general ledger through Accounting mappings.',
    images: [
      '/images/products/revenues/revenue-items.png',
      '/images/products/revenues/revenue-item-form.png',
      '/images/products/revenues/revenue-taxes.png',
      '/images/products/revenues/revenue-tax-form.png',
      '/images/products/revenues/revenue-coupons.png',
      '/images/products/revenues/revenue-coupon-form.png',
      '/images/products/revenues/revenue-payment-acceptance.png',
      '/images/products/revenues/revenue-sale-orders.png',
      '/images/products/revenues/revenue-sale-order-form.png',
      '/images/products/revenues/revenue-invoices.png',
      '/images/products/revenues/revenue-invoice-form.png',
      '/images/products/revenues/revenue-receipts.png',
      '/images/products/revenues/revenue-receipt-form.png',
      '/images/products/revenues/revenue-customer-deposits.png',
      '/images/products/revenues/revenue-customer-deposit-form.png',
      '/images/products/revenues/revenue-deposit-applications.png',
      '/images/products/revenues/revenue-deposit-application-form.png',
      '/images/products/revenues/revenue-deposit-refunds.png',
      '/images/products/revenues/revenue-deposit-refund-form.png',
      '/images/products/revenues/revenue-ar-query.png',
      '/images/products/revenues/revenue-item-sales-history.png',
      '/images/products/revenues/revenue-reports.png',
    ],
    backgroundImage: '/images/products/revenues/revenue-sale-orders.png',
    highlights: [
      'Sales orders, invoices, receipts, and customer deposits',
      'Items with price levels, taxes, coupons, and categories',
      'KHQR and other payment-acceptance methods',
      'Receivables query and revenue reports',
    ],
    workflow: {
      title: 'Quote to cash',
      path: 'Quotation → Sales order → Invoice → Receipt',
      steps: [
        {
          title: 'Quote in CRM',
          description: 'Send a priced quotation. When it is won, convert it without re-entering lines.',
        },
        {
          title: 'Confirm the sales order',
          description: 'The customer confirms; the order is approved and can reserve stock.',
        },
        {
          title: 'Invoice',
          description: 'Bill for goods or services. Stock on inventory items is deducted on invoice.',
        },
        {
          title: 'Collect',
          description: 'Record receipts, apply customer deposits, or refund unused deposits. Query AR anytime.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Setup',
        items: [
          'Items and services, including price levels',
          'VAT and sales tax definitions',
          'Coupons / discount codes',
          'Payment acceptance such as KHQR',
          'Categories with optional GL overrides',
        ],
      },
      {
        title: 'Transactions and reports',
        items: [
          'Sales orders, invoices, and receipts',
          'Customer deposits, deposit applications, and deposit refunds',
          'Receivables query (AR)',
          'Item sales history by detail or by customer',
          'Revenue reports',
        ],
      },
    ],
    mainFeatures: [
      {
        icon: '📦',
        title: 'Items, taxes, and price levels',
        description:
          'Maintain the products and services you sell, with price levels, VAT, coupons, and categories. The same item catalog feeds quotations, invoices, POS, and the online store.',
        images: [
          '/images/products/revenues/revenue-items.png',
          '/images/products/revenues/revenue-item-form.png',
          '/images/products/revenues/revenue-taxes.png',
          '/images/products/revenues/revenue-tax-form.png',
          '/images/products/revenues/revenue-coupons.png',
          '/images/products/revenues/revenue-coupon-form.png',
          '/images/products/revenues/revenue-payment-acceptance.png',
        ],
      },
      {
        icon: '🧾',
        title: 'Orders and invoices',
        description:
          'Confirm sales orders from won quotations, then invoice. Inventory items deduct stock when invoiced so warehouse balances stay aligned with billing.',
        images: [
          '/images/products/revenues/revenue-sale-orders.png',
          '/images/products/revenues/revenue-sale-order-form.png',
          '/images/products/revenues/revenue-invoices.png',
          '/images/products/revenues/revenue-invoice-form.png',
        ],
      },
      {
        icon: '💳',
        title: 'Receipts, deposits, and KHQR',
        description:
          'Record payments against invoices, take customer deposits and apply or refund them later, and accept scan-to-pay methods such as KHQR. Approved invoices get a share link: your customer opens a clean invoice page on their phone, prints the PDF, and can pay in one click — cash arrives sooner.',
        images: [
          '/images/products/revenues/revenue-receipts.png',
          '/images/products/revenues/revenue-receipt-form.png',
          '/images/products/revenues/revenue-customer-deposits.png',
          '/images/products/revenues/revenue-customer-deposit-form.png',
          '/images/products/revenues/revenue-deposit-applications.png',
          '/images/products/revenues/revenue-deposit-application-form.png',
          '/images/products/revenues/revenue-deposit-refunds.png',
          '/images/products/revenues/revenue-deposit-refund-form.png',
        ],
      },
      {
        icon: '📊',
        title: 'Receivables and reports',
        description:
          'See every unpaid bill aged by customer and age — so credit control knows exactly who to call. VAT and withholding sale journals for tax filing come out ready, and mapped invoices post straight into Accounting for the income statement and cash flow.',
        images: [
          '/images/products/revenues/revenue-ar-query.png',
          '/images/products/revenues/revenue-item-sales-history.png',
          '/images/products/revenues/revenue-reports.png',
        ],
      },
    ],
    meta: {
      title: 'AKAS Revenues | Sales orders, invoicing, receipts and receivables',
      description:
        'AKAS Revenues manages items, sales orders, invoices, receipts, customer deposits, KHQR payments, and accounts receivable — posting to the general ledger when mapped.',
      keywords:
        'invoicing, sales orders, receipts, KHQR, accounts receivable, VAT, AKAS',
    },
    additionalFeatures: [
      {
        icon: '💱',
        title: 'Multi-currency',
        description: 'Price and collect in configured currencies with exchange rates from setup.',
      },
      {
        icon: '🌐',
        title: 'Multilingual',
        description: 'Documents and screens in the languages your team and customers use.',
      },
      {
        icon: '✓',
        title: 'Approvals',
        description: 'Approve orders and invoices through the same workflow engine as the rest of AKAS.',
      },
      {
        icon: '📒',
        title: 'GL posting',
        description: 'Transaction mappings send approved invoices and receipts to journals.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'Cash you can see',
        description: 'Orders, invoices, receipts, and deposits are one chain — AR is not a separate workbook.',
      },
      {
        number: '02',
        title: 'Stock that matches billing',
        description: 'Invoicing inventory items deducts warehouse balances automatically.',
      },
      {
        number: '03',
        title: 'Local payments',
        description: 'KHQR and other acceptance methods sit next to classic receipts.',
      },
      {
        number: '04',
        title: 'Books that follow sales',
        description: 'Mapped transactions post to Accounting so P&L is not typed in twice.',
      },
    ],
    related: [
      { label: 'CRM', to: '/products/crm' },
      { label: 'Inventory', to: '/products/inventory' },
      { label: 'Accounting', to: '/products/accounting' },
      { label: 'Revenues user guide', href: `${DOCS_ORIGIN}/en/revenue/` },
    ],
  },
  {
    id: 5,
    slug: 'expenses',
    name: 'Expenses & Payables',
    shortName: 'Expenses',
    navLabel: 'Expenses & Procurement',
    tagline: 'Vendors, bills, payments, and AP',
    featured: true,
    docsUrl: `${DOCS_ORIGIN}/en/expense/`,
    description:
      '**Expenses** manages accounts payable in AKAS, from vendor records and expense items to vendor bills, payments, and deposits. Record supplier invoices, pay and allocate bills, or make advance deposits and apply them to future bills or request refunds. Use payables queries and expense reports to track outstanding balances and expenses. Approved bills and payments are posted to the general ledger through Accounting mappings. Purchase orders and goods receipts are managed separately in Inventory.',
    images: [
      '/images/products/expenses/expense-vendors.png',
      '/images/products/expenses/expense-vendor-form.png',
      '/images/products/expenses/expense-items.png',
      '/images/products/expenses/expense-item-form.png',
      '/images/products/expenses/expense-bills.png',
      '/images/products/expenses/expense-bill-form.png',
      '/images/products/expenses/expense-payments.png',
      '/images/products/expenses/expense-payment-form.png',
      '/images/products/expenses/expense-vendor-deposits.png',
      '/images/products/expenses/expense-vendor-deposit-form.png',
      '/images/products/expenses/expense-vendor-deposit-applications.png',
      '/images/products/expenses/expense-vendor-deposit-application-form.png',
      '/images/products/expenses/expense-vendor-deposit-refunds.png',
      '/images/products/expenses/expense-vendor-deposit-refund-form.png',
      '/images/products/expenses/expense-ap-query.png',
      '/images/products/expenses/expense-reports.png',
    ],
    backgroundImage: '/images/products/expenses/expense-bills.png',
    highlights: [
      'Vendor master, bills, and bill payments',
      'Vendor deposits with apply and refund',
      'Payables query and expense reports',
      'Posts to Accounting when GL mappings are set',
    ],
    workflow: {
      title: 'Procure to pay',
      path: 'Vendor → Bill → Payment  (or Vendor deposit → Apply / Refund)',
      steps: [
        {
          title: 'Register the vendor',
          description: 'Keep supplier details, terms, and contacts once — reused by inventory purchasing and fixed assets.',
        },
        {
          title: 'Record the bill',
          description: 'Enter the vendor invoice against expense items or received goods.',
        },
        {
          title: 'Pay and allocate',
          description: 'Record bill payments and allocate them to open bills.',
        },
        {
          title: 'Deposits when you prepay',
          description: 'Take a vendor deposit, apply it to later bills, or refund the unused amount.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Setup',
        items: [
          'Expense items and services you purchase',
          'Vendor master data',
          'Shared categories with optional GL overrides',
        ],
      },
      {
        title: 'Transactions and reports',
        items: [
          'Bills and bill payments',
          'Vendor deposits, applications, and refunds',
          'Payables query (AP)',
          'Expense reports',
        ],
      },
    ],
    mainFeatures: [
      {
        icon: '👥',
        title: 'Vendor management',
        description:
          'One supplier record for bills, payments, purchase orders, and asset purchases — including terms used when you pay.',
        images: [
          '/images/products/expenses/expense-vendors.png',
          '/images/products/expenses/expense-vendor-form.png',
        ],
      },
      {
        icon: '📄',
        title: 'Bills and payments',
        description:
          'Record vendor invoices, pay them, and allocate payments to open bills so AP aging stays accurate.',
        images: [
          '/images/products/expenses/expense-bills.png',
          '/images/products/expenses/expense-bill-form.png',
          '/images/products/expenses/expense-payments.png',
          '/images/products/expenses/expense-payment-form.png',
        ],
      },
      {
        icon: '💰',
        title: 'Vendor deposits',
        description:
          'Prepay a supplier, apply the deposit to later bills, or refund what you did not use — with a full trail.',
        images: [
          '/images/products/expenses/expense-vendor-deposits.png',
          '/images/products/expenses/expense-vendor-deposit-form.png',
          '/images/products/expenses/expense-vendor-deposit-applications.png',
          '/images/products/expenses/expense-vendor-deposit-application-form.png',
          '/images/products/expenses/expense-vendor-deposit-refunds.png',
          '/images/products/expenses/expense-vendor-deposit-refund-form.png',
        ],
      },
      {
        icon: '📊',
        title: 'Payables query and reports',
        description:
          'See what is outstanding, by vendor and due date. Mapped bills and payments post journals for cash flow and P&L.',
        images: [
          '/images/products/expenses/expense-ap-query.png',
          '/images/products/expenses/expense-reports.png',
        ],
      },
    ],
    meta: {
      title: 'AKAS Expenses | Vendors, bills, payments and accounts payable',
      description:
        'AKAS Expenses tracks vendors, bills, bill payments, vendor deposits, and accounts payable, with optional posting to the general ledger.',
      keywords:
        'accounts payable, vendor bills, bill payments, expenses, procurement, AKAS',
    },
    additionalFeatures: [
      {
        icon: '📦',
        title: 'Tied to inventory',
        description: 'Purchase orders and goods receipts in Inventory use the same vendors.',
      },
      {
        icon: '✓',
        title: 'Approvals',
        description: 'Route bills and payments through workflow before they post.',
      },
      {
        icon: '📒',
        title: 'GL posting',
        description: 'Account mappings send approved payables into Accounting journals.',
      },
      {
        icon: '🌐',
        title: 'Multilingual',
        description: 'Finance and operations can work in either language.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'AP that matches the warehouse',
        description: 'Vendors, POs, receipts, and bills share masters so three-way checking is possible.',
      },
      {
        number: '02',
        title: 'Deposits are not lost',
        description: 'Prepayments are applied or refunded instead of sitting in a suspense account nobody reconciles.',
      },
      {
        number: '03',
        title: 'Pay what you actually owe',
        description: 'Payables query shows open bills before you release payment.',
      },
      {
        number: '04',
        title: 'Books follow spending',
        description: 'Mapped bills and payments hit the ledger without a month-end copy-paste.',
      },
    ],
    related: [
      { label: 'Inventory', to: '/products/inventory' },
      { label: 'Accounting', to: '/products/accounting' },
      { label: 'Expenses user guide', href: `${DOCS_ORIGIN}/en/expense/` },
    ],
  },
  {
    id: 6,
    slug: 'inventory',
    name: 'Inventory',
    shortName: 'Inventory',
    navLabel: 'Inventory',
    tagline: 'Stock, purchase orders, receipts, transfers, and balances',
    featured: true,
    docsUrl: `${DOCS_ORIGIN}/en/inventory/`,
    description:
      'Inventory manages physical stock across locations: items that track quantity, purchase orders, goods received, transfers, adjustments, withdrawals, and on-hand balances. Selling through Revenues deducts stock. Employees can request an inventory withdrawal from self-service; approved requests appear as withdrawals for the warehouse. Locations come from System Administration so stores and warehouses stay on the org map.',
    images: [
      '/images/products/inventory/inventory-items.png',
      '/images/products/inventory/inventory-item-form.png',
      '/images/products/inventory/inventory-purchase-orders.png',
      '/images/products/inventory/inventory-purchase-order-form.png',
      '/images/products/inventory/inventory-received.png',
      '/images/products/inventory/inventory-received-form.png',
      '/images/products/inventory/inventory-transfers.png',
      '/images/products/inventory/inventory-transfer-form.png',
      '/images/products/inventory/inventory-adjustments.png',
      '/images/products/inventory/inventory-adjustment-form.png',
      '/images/products/inventory/inventory-withdrawals.png',
      '/images/products/inventory/inventory-withdrawal-form.png',
      '/images/products/inventory/inventory-balances.png',
      '/images/products/inventory/inventory-reports.png',
    ],
    backgroundImage: '/images/products/inventory/inventory-items.png',
    highlights: [
      'Purchase orders → goods received → on-hand balances',
      'Transfers, adjustments, and withdrawals',
      'Stock deducted when inventory items are invoiced',
      'Employee self-service withdrawal requests',
    ],
    workflow: {
      title: 'Buy, hold, sell',
      path: 'Purchase order → Receive → Sell (invoice) → Verify balances',
      steps: [
        {
          title: 'Order from the vendor',
          description: 'Raise a purchase order against inventory items and a supplier from Expenses.',
        },
        {
          title: 'Receive into a location',
          description: 'Record goods received into a warehouse or store. On-hand quantity increases.',
        },
        {
          title: 'Move, adjust, or withdraw',
          description: 'Transfer between locations, adjust for counts, or issue withdrawals (including self-service requests).',
        },
        {
          title: 'Sell and check balances',
          description: 'Invoices in Revenues deduct stock. Inventory balances confirm what is still on hand.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Setup',
        items: [
          'Inventory items that track stock',
          'Locations (warehouses and stores) from Administration',
          'Vendors shared with Expenses',
          'Categories with optional GL overrides',
        ],
      },
      {
        title: 'Transactions and reports',
        items: [
          'Purchase orders and inventories received',
          'Transfers, adjustments, and withdrawals',
          'Inventory balances',
          'Inventory reports',
        ],
      },
    ],
    mainFeatures: [
      {
        icon: '📦',
        title: 'Multi-location stock',
        description:
          'Track on-hand quantity by warehouse or store. Locations are the same records used across the ERP, not a separate warehouse app.',
        images: [
          '/images/products/inventory/inventory-items.png',
          '/images/products/inventory/inventory-item-form.png',
          '/images/products/inventory/inventory-balances.png',
        ],
      },
      {
        icon: '🧾',
        title: 'Purchasing and receiving',
        description:
          'Issue purchase orders, then receive goods into a location. Vendor bills in Expenses can follow the receipt.',
        images: [
          '/images/products/inventory/inventory-purchase-orders.png',
          '/images/products/inventory/inventory-purchase-order-form.png',
          '/images/products/inventory/inventory-received.png',
          '/images/products/inventory/inventory-received-form.png',
        ],
      },
      {
        icon: '🔄',
        title: 'Transfers, adjustments, withdrawals',
        description:
          'Move stock between locations, adjust after a count, or issue withdrawals. Employees can request a withdrawal from self-service; warehouse staff fulfill the approved request.',
        images: [
          '/images/products/inventory/inventory-transfers.png',
          '/images/products/inventory/inventory-transfer-form.png',
          '/images/products/inventory/inventory-adjustments.png',
          '/images/products/inventory/inventory-adjustment-form.png',
          '/images/products/inventory/inventory-withdrawals.png',
          '/images/products/inventory/inventory-withdrawal-form.png',
        ],
      },
      {
        icon: '📊',
        title: 'Balances and reports',
        description:
          'Verify on-hand quantities after receiving, transferring, and selling. Inventory reports show movement and remaining stock.',
        images: [
          '/images/products/inventory/inventory-balances.png',
          '/images/products/inventory/inventory-reports.png',
        ],
      },
    ],
    meta: {
      title: 'AKAS Inventory | Purchase orders, receipts, transfers and balances',
      description:
        'AKAS Inventory manages stock items, purchase orders, goods received, transfers, adjustments, withdrawals, and balances across locations.',
      keywords:
        'inventory, warehouse, purchase orders, stock transfers, inventory balances, AKAS',
    },
    additionalFeatures: [
      {
        icon: '🛒',
        title: 'Tied to selling',
        description: 'Invoicing inventory items in Revenues deducts stock automatically.',
      },
      {
        icon: '💻',
        title: 'Self-service withdrawals',
        description: 'Staff request stock from Dashboard → Self Service → Inventory Withdrawal Request.',
      },
      {
        icon: '🔔',
        title: 'Approvals and logs',
        description: 'Movements can require approval; data logs show who changed stock.',
      },
      {
        icon: '🏪',
        title: 'POS and store',
        description: 'The same items and locations feed Point of Sale and the online store.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'Know what is on hand',
        description: 'Balances update from receipts, transfers, withdrawals, and sales — not from a weekly recount only.',
      },
      {
        number: '02',
        title: 'Purchasing with a trail',
        description: 'POs and receipts sit next to vendor bills instead of in a separate purchasing tool.',
      },
      {
        number: '03',
        title: 'Fewer surprise stockouts',
        description: 'Selling deducts inventory items so the next POS or invoice sale sees the truth.',
      },
      {
        number: '04',
        title: 'Staff can request stock',
        description: 'Self-service withdrawals keep internal consumption on the same ledger as warehouse issues.',
      },
    ],
    related: [
      { label: 'Expenses', to: '/products/expenses' },
      { label: 'Revenues', to: '/products/revenues' },
      { label: 'Point of Sale', to: '/products/pos' },
      { label: 'Inventory user guide', href: `${DOCS_ORIGIN}/en/inventory/` },
    ],
  },
  {
    id: 7,
    slug: 'clinic',
    name: 'Clinic Management',
    shortName: 'Clinic',
    navLabel: 'Clinic Management',
    tagline: 'Queue, consult, prescribe, dispense, and collect',
    featured: true,
    description:
      'AKAS Clinic Management runs the front desk through payment: queues, appointments, electronic records, consultation and prescription, dispensary stock, invoicing, and KHQR collection. It is multilingual and works in the browser on clinic PCs and tablets.',
    images: [
      '/images/products/clinic-queue.png',
      '/images/products/clinic-appointment.png',
      '/images/products/clinic-consultation.png',
      '/images/products/clinic-prescription.png',
      '/images/products/clinic-dispensary.png',
      '/images/products/clinic-payment.png',
      '/images/products/clinic-dashboard.png',
    ],
    backgroundImage: '/images/products/clinic-bg.gif',
    highlights: [
      'Queue and appointment management',
      'Consultation, prescription, and EHR',
      'Dispensary inventory',
      'Invoicing with KHQR payment',
    ],
    workflow: {
      title: 'Patient visit',
      path: 'Queue / appointment → Consult → Prescribe → Dispense → Pay',
      steps: [
        {
          title: 'Arrive',
          description: 'Put the patient on the queue or honor a booked appointment.',
        },
        {
          title: 'Consult and record',
          description: 'Capture the visit in the electronic record and write a prescription.',
        },
        {
          title: 'Dispense',
          description: 'Issue medicines from dispensary stock with a movement trail.',
        },
        {
          title: 'Collect',
          description: 'Invoice the visit and take payment, including KHQR.',
        },
      ],
    },
    mainFeatures: [
      {
        icon: '💻',
        title: 'Queue management',
        description:
          'Track who is waiting in real time so the floor can pull the next patient without shouting across the waiting room.',
        images: ['/images/products/clinic-queue.png'],
      },
      {
        icon: '📅',
        title: 'Appointment scheduling',
        description:
          'Book visits, see the day at a glance, and connect the appointment to the patient record when they arrive.',
        images: ['/images/products/clinic-appointment.png'],
      },
      {
        icon: '📊',
        title: 'Electronic health records',
        description:
          'Store diagnoses, treatments, medications, and visit history on the patient — available to the next clinician who opens the chart.',
        images: ['/images/products/clinic-ehr.png'],
      },
      {
        icon: '🩺',
        title: 'Consultation and prescription',
        description:
          'Document the consult and issue prescriptions that the dispensary can fulfill without retyping drugs and doses.',
        images: [
          '/images/products/clinic-consultation.png',
          '/images/products/clinic-prescription.png',
        ],
      },
      {
        icon: '💊',
        title: 'Dispensary management',
        description:
          'Keep clinic pharmacy stock current: issues, adjustments, and a trail of what left the shelf.',
        images: ['/images/products/clinic-dispensary.png'],
      },
      {
        icon: '💳',
        title: 'Invoicing and payment',
        description:
          'Generate invoices and receipts for consultations and treatments, and collect through methods such as KHQR.',
        images: ['/images/products/clinic-payment.png'],
      },
    ],
    meta: {
      title: 'AKAS Clinic Management | Queue, EHR, dispensary and KHQR billing',
      description:
        'AKAS Clinic Management covers queues, appointments, electronic records, prescriptions, dispensary stock, invoicing, and KHQR payment.',
      keywords:
        'clinic management, EHR, appointment, dispensary, KHQR clinic, AKAS',
    },
    additionalFeatures: [
      {
        icon: '🌐',
        title: 'Multilingual',
        description: 'Front desk and clinicians can work in either language.',
      },
      {
        icon: '📁',
        title: 'Document management',
        description: 'Keep visit notes, files, and history on the patient record.',
      },
      {
        icon: '⚙️',
        title: 'Customizable',
        description: 'Add fields and reports for how your clinic actually works.',
      },
      {
        icon: '🔔',
        title: 'Live notifications',
        description: 'Queue, appointment, and stock events surface as they happen.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'Shorter waits',
        description: 'Queues and appointments give the floor a single source of who is next.',
      },
      {
        number: '02',
        title: 'One patient chart',
        description: 'Consult, prescription, and dispensary issue sit on the same visit.',
      },
      {
        number: '03',
        title: 'Stock that matches the script',
        description: 'Dispensary movements follow the prescription instead of a side notebook.',
      },
      {
        number: '04',
        title: 'Collect before they leave',
        description: 'Invoice and KHQR payment close the visit without a separate cashier system.',
      },
    ],
    related: [
      { label: 'Live clinic demo', to: '/demo' },
      { label: 'Revenues', to: '/products/revenues' },
    ],
  },
  {
    id: 8,
    slug: 'pos',
    name: 'Point of Sale',
    shortName: 'POS',
    navLabel: 'Point of Sales (POS)',
    tagline: 'Mart and restaurant checkout on Windows, macOS, and Android',
    featured: true,
    docsUrl: `${DOCS_ORIGIN}/en/pos/`,
    description:
      'AKAS POS is built for daily retail (Mart) and restaurant operations. Cashiers log in, choose a mode, and sell: search or scan items, take payment, and print. Restaurant mode adds floors, tables, orders, and kitchen printing. Stock, prices, and items come from the same catalog as Revenues and Inventory, so a till sale is not a separate island of data.',
    images: [
      '/images/products/pos/pos-login.png',
      '/images/products/pos/pos-mart-main.png',
      '/images/products/pos/pos-mart-checkout.png',
      '/images/products/pos/pos-mart-checkout-final.png',
      '/images/products/pos/pos-rest.png',
      '/images/products/pos/pos-rest-main.png',
      '/images/products/pos/pos-rest-floor.png',
      '/images/products/pos/pos-rest-floor-create.png',
      '/images/products/pos/pos-rest-table-create.png',
      '/images/products/pos/pos-rest-table-move-resize.png',
      '/images/products/pos/pos-rest-order.png',
      '/images/products/pos/pos-rest-order-2.png',
      '/images/products/pos/pos-rest-checkout.png',
      '/images/products/pos/pos-rest-checkout-complete.png',
      '/images/products/pos/pos-rest-order-kitchen-print.png',
      '/images/products/pos/pos-rest-order-kitchen-print-2.png',
      '/images/products/pos/pos-rest-order-kitchen-reprint.png',
    ],
    backgroundImage: '/images/products/pos/pos-mart-main.png',
    highlights: [
      'Mart mode: catalog, barcode, checkout, payment',
      'Restaurant mode: floors, tables, kitchen print, payment',
      'Native apps for Windows, macOS, and Android',
      'Items and stock shared with Inventory and Revenues',
    ],
    workflow: {
      title: 'Take a sale',
      path: 'Login → Mart or Restaurant → Add items / table order → Checkout → Payment',
      steps: [
        {
          title: 'Login and choose mode',
          description: 'Open POS and select Mart (retail) or Restaurant for the shift.',
        },
        {
          title: 'Build the ticket',
          description:
            'Mart: search, scan, and add products from the catalog. Restaurant: seat a table, take the order, print to kitchen.',
        },
        {
          title: 'Checkout',
          description: 'Review totals, taxes, and tender. Complete payment and issue the receipt.',
        },
        {
          title: 'Stock and books follow',
          description: 'Sold items update inventory and flow into revenue records used by Accounting.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Mart (retail)',
        items: [
          'Header search and barcode scan',
          'Category filters and product tiles',
          'Current-sale panel with tax and total',
          'Fast checkout — a simple keyboard shortcut gets the cashier through the queue',
        ],
      },
      {
        title: 'Restaurant',
        items: [
          'Visual floor and table layout — add floors, place tables where they really are',
          'Table orders with kitchen printing; reprints include only the new items, so the kitchen never cooks an order twice',
          'Full-ticket reprint when a receipt goes missing',
          'Checkout and payment at the table or cashier, with coupons and tax on the receipt',
        ],
      },
    ],
    mainFeatures: [
      {
        icon: '🖥️',
        title: 'Multi-OS apps',
        description:
          'Run AKAS POS on Windows tills, Mac counters, and Android tablets with the same checkout flow.',
        images: ['/images/products/pos/pos-login.png'],
      },
      {
        icon: '🛒',
        title: 'Mart checkout',
        description:
          'Find products, scan barcodes, add them to the sale, and complete payment. The screen is split into search, catalog, and the current ticket.',
        images: [
          '/images/products/pos/pos-mart-main.png',
          '/images/products/pos/pos-mart-checkout.png',
          '/images/products/pos/pos-mart-checkout-final.png',
        ],
      },
      {
        icon: '🍽️',
        title: 'Restaurant service',
        description:
          'Manage floors and tables, take orders, print to the kitchen, reprint when a ticket is lost, and take payment.',
        images: [
          '/images/products/pos/pos-rest.png',
          '/images/products/pos/pos-rest-main.png',
          '/images/products/pos/pos-rest-floor.png',
          '/images/products/pos/pos-rest-floor-create.png',
          '/images/products/pos/pos-rest-table-create.png',
          '/images/products/pos/pos-rest-table-move-resize.png',
          '/images/products/pos/pos-rest-order.png',
          '/images/products/pos/pos-rest-order-2.png',
          '/images/products/pos/pos-rest-checkout.png',
          '/images/products/pos/pos-rest-checkout-complete.png',
          '/images/products/pos/pos-rest-order-kitchen-print.png',
          '/images/products/pos/pos-rest-order-kitchen-print-2.png',
          '/images/products/pos/pos-rest-order-kitchen-reprint.png',
        ],
      },
      {
        icon: '📦',
        title: 'Inventory sync',
        description:
          'Sold items come from the shared catalog. Stock moves with the sale so back office and the floor agree.',
      },
    ],
    meta: {
      title: 'AKAS POS | Mart and restaurant point of sale',
      description:
        'AKAS POS supports retail Mart checkout and restaurant table service with kitchen printing, on Windows, macOS, and Android, using the same items as Inventory and Revenues.',
      keywords:
        'POS, restaurant POS, retail POS, kitchen print, barcode, Windows POS, Android POS, AKAS',
    },
    additionalFeatures: [
      {
        icon: '🌐',
        title: 'Multilingual',
        description: 'Cashiers can work in either language.',
      },
      {
        icon: '⚡',
        title: 'Offline-capable',
        description: 'Keep selling if the line drops, then sync when you are back.',
      },
      {
        icon: '🔌',
        title: 'Hardware',
        description: 'Connect scanners, receipt printers, and cash drawers.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'Use the hardware you have',
        description: 'Windows, macOS, or Android — no forced till replacement.',
      },
      {
        number: '02',
        title: 'Retail and F&B in one product',
        description: 'Mart and restaurant modes share login, items, and payment instead of two vendors.',
      },
      {
        number: '03',
        title: 'Kitchen that hears the order',
        description: 'Restaurant print and reprint keep the pass moving.',
      },
      {
        number: '04',
        title: 'The till is part of the ERP',
        description: 'Sales hit inventory and revenue, not a side spreadsheet at close.',
      },
    ],
    related: [
      { label: 'Revenues', to: '/products/revenues' },
      { label: 'Inventory', to: '/products/inventory' },
      { label: 'Online store', to: '/products/online-store' },
      { label: 'POS user guide', href: `${DOCS_ORIGIN}/en/pos/` },
    ],
  },
  {
    id: 9,
    slug: 'accounting',
    name: 'Accounting',
    shortName: 'Accounting',
    navLabel: 'Accounting',
    tagline: 'General ledger, journals, budgets, and financial statements',
    featured: true,
    docsUrl: `${DOCS_ORIGIN}/en/accounting/`,
    description:
      'Accounting is the general ledger for AKAS. You set up the chart of accounts, journals, fiscal years, and GL mappings. Approved invoices, bills, receipts, and payments from Revenues and Expenses post journal entries. Accountants review or enter manual journals, then run income statement, balance sheet, cash flow, trial balance, and budget vs actual. Menu items follow role permissions; complete fiscal years and org units in setup before posting.',
    images: [
      '/images/products/accounting/accounting-coa.png',
      '/images/products/accounting/accounting-coa-form.png',
      '/images/products/accounting/accounting-journals.png',
      '/images/products/accounting/accounting-journal-form.png',
      '/images/products/accounting/accounting-journal-entries.png',
      '/images/products/accounting/accounting-journal-entry-form.png',
      '/images/products/accounting/accounting-journal-mappings.png',
      '/images/products/accounting/accounting-journal-mapping-form.png',
      '/images/products/accounting/accounting-txn-mappings.png',
      '/images/products/accounting/accounting-budgets.png',
      '/images/products/accounting/accounting-budget-form.png',
      '/images/products/accounting/accounting-budget-vs-actual.png',
      '/images/products/accounting/accounting-income-statement.png',
      '/images/products/accounting/accounting-balance-sheet.png',
      '/images/products/accounting/accounting-cash-flow.png',
      '/images/products/accounting/accounting-trial-balance.png',
    ],
    backgroundImage: '/images/products/accounting/accounting-coa.png',
    highlights: [
      'Chart of accounts, journals, and budgets',
      'GL mappings from invoices, bills, and payments',
      'Income statement, balance sheet, cash flow, trial balance',
      'Budget vs actual',
    ],
    workflow: {
      title: 'Close the books from operations',
      path: 'Chart of accounts → GL mappings → Operational posting → Statements',
      steps: [
        {
          title: 'Structure the ledger',
          description: 'Build the chart of accounts and define journals, fiscal years, and budgets.',
        },
        {
          title: 'Map transactions',
          description: 'Tell AKAS which GL accounts invoices, bills, receipts, and payments should hit.',
        },
        {
          title: 'Operate',
          description: 'Revenues and Expenses post journal entries when documents are approved.',
        },
        {
          title: 'Review and report',
          description:
            'Inspect journal entries, add manual adjustments, then run financial statements and budget vs actual.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Setup',
        items: [
          'Chart of accounts',
          'Journals',
          'Budgets',
          'GL account mappings for operational documents',
          'GL journal mappings',
        ],
      },
      {
        title: 'Transactions and statements',
        items: [
          'Journal entries (review operational posts and enter adjustments)',
          'Income statement',
          'Balance sheet',
          'Cash flow statement',
          'Trial balance',
          'Budget vs actual',
        ],
      },
    ],
    mainFeatures: [
      {
        icon: '📒',
        title: 'Chart of accounts and journals',
        description:
          'Define the account structure and the journals that entries post into. Fiscal years and org units in Administration gate when posting is allowed.',
        images: [
          '/images/products/accounting/accounting-coa.png',
          '/images/products/accounting/accounting-coa-form.png',
          '/images/products/accounting/accounting-journals.png',
          '/images/products/accounting/accounting-journal-form.png',
        ],
      },
      {
        icon: '🔗',
        title: 'GL mappings',
        description:
          'Map invoices, bills, receipts, and payments to accounts so operations post the ledger instead of finance retyping it.',
        images: [
          '/images/products/accounting/accounting-journal-mappings.png',
          '/images/products/accounting/accounting-journal-mapping-form.png',
          '/images/products/accounting/accounting-txn-mappings.png',
        ],
      },
      {
        icon: '✍️',
        title: 'Journal entries',
        description:
          'Review auto-posted entries and create manual adjustments when you need a correcting journal.',
        images: [
          '/images/products/accounting/accounting-journal-entries.png',
          '/images/products/accounting/accounting-journal-entry-form.png',
        ],
      },
      {
        icon: '📈',
        title: 'Financial statements',
        description:
          'Income statement, balance sheet, cash flow, trial balance, and budget vs actual — from the same ledger operational modules post into.',
        images: [
          '/images/products/accounting/accounting-income-statement.png',
          '/images/products/accounting/accounting-balance-sheet.png',
          '/images/products/accounting/accounting-cash-flow.png',
          '/images/products/accounting/accounting-trial-balance.png',
          '/images/products/accounting/accounting-budget-vs-actual.png',
        ],
      },
    ],
    meta: {
      title: 'AKAS Accounting | General ledger, journals and financial statements',
      description:
        'AKAS Accounting manages the chart of accounts, GL mappings, journal entries, budgets, and statements including income statement, balance sheet, cash flow, and trial balance.',
      keywords:
        'accounting, general ledger, income statement, balance sheet, cash flow, trial balance, AKAS ERP',
    },
    additionalFeatures: [
      {
        icon: '🏢',
        title: 'Org and fiscal setup',
        description: 'Legal entities, org units, and fiscal years from Administration before you post.',
      },
      {
        icon: '🔒',
        title: 'Role permissions',
        description: 'Statement and journal menus appear based on the user’s role.',
      },
      {
        icon: '🏗️',
        title: 'Fixed assets',
        description: 'Depreciation posts into the same ledger once asset classes are mapped.',
      },
      {
        icon: '🌐',
        title: 'Multilingual',
        description: 'Finance teams can work in either language.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'No double entry from ops',
        description: 'Mapped sales and payables post journals when documents are approved.',
      },
      {
        number: '02',
        title: 'Statements from one ledger',
        description: 'P&L, balance sheet, and cash flow are views of the same journals.',
      },
      {
        number: '03',
        title: 'Budgets you can compare',
        description: 'Budget vs actual is a first-class report, not an export to Excel.',
      },
      {
        number: '04',
        title: 'Adjustments stay auditable',
        description: 'Manual journals sit beside operational posts with user and timestamp.',
      },
    ],
    related: [
      { label: 'Revenues', to: '/products/revenues' },
      { label: 'Expenses', to: '/products/expenses' },
      { label: 'Fixed assets', to: '/products/fixed-assets' },
      { label: 'Accounting user guide', href: `${DOCS_ORIGIN}/en/accounting/` },
    ],
  },
  {
    id: 10,
    slug: 'fixed-assets',
    name: 'Fixed Assets',
    shortName: 'Fixed Assets',
    navLabel: 'Fixed Assets',
    tagline: 'Register assets, class them, and post depreciation',
    featured: true,
    docsUrl: `${DOCS_ORIGIN}/en/fixed-assets/`,
    description:
      'Fixed Assets manages long-term assets and depreciation. Define asset classes with useful life, register each asset (with location and vendor when relevant), then calculate periodic depreciation into the general ledger. Chart of accounts setup in Accounting is recommended before you post depreciation.',
    images: [
      '/images/products/fixed-assets/fa-asset-classes.png',
      '/images/products/fixed-assets/fa-asset-class-form.png',
      '/images/products/fixed-assets/fa-assets.png',
      '/images/products/fixed-assets/fa-asset-form.png',
      '/images/products/fixed-assets/fa-depreciation.png',
      '/images/products/fixed-assets/fa-reports.png',
    ],
    backgroundImage: '/images/products/fixed-assets/fa-assets.png',
    highlights: [
      'Asset classes with useful life',
      'Asset register with location and vendor',
      'Periodic depreciation posting',
      'Fixed asset reports',
    ],
    workflow: {
      title: 'Capitalize and depreciate',
      path: 'Asset class → Register asset → Calculate depreciation',
      steps: [
        {
          title: 'Define the class',
          description: 'Set category and useful life so similar assets depreciate the same way.',
        },
        {
          title: 'Register the asset',
          description: 'Record the individual asset, location, and vendor as needed.',
        },
        {
          title: 'Run depreciation',
          description: 'Calculate and post periodic depreciation entries to the ledger.',
        },
        {
          title: 'Report',
          description: 'Use fixed asset reports for the register and accumulated depreciation.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Pages',
        items: [
          'Asset classes',
          'Fixed assets register',
          'Calculate depreciation',
          'Fixed asset reports',
          'Locations and vendors from the shared setup menus',
        ],
      },
    ],
    mainFeatures: [
      {
        icon: '🏷️',
        title: 'Asset classes',
        description: 'Group assets by category and useful life so depreciation policy is not per-spreadsheet.',
        images: [
          '/images/products/fixed-assets/fa-asset-classes.png',
          '/images/products/fixed-assets/fa-asset-class-form.png',
        ],
      },
      {
        icon: '📋',
        title: 'Asset register',
        description: 'Each asset has an identity, class, and optional location/vendor — the same locations and vendors used elsewhere in AKAS.',
        images: [
          '/images/products/fixed-assets/fa-assets.png',
          '/images/products/fixed-assets/fa-asset-form.png',
        ],
      },
      {
        icon: '📉',
        title: 'Depreciation',
        description: 'Post periodic depreciation into Accounting instead of adjusting the books by hand at year end.',
        images: [
          '/images/products/fixed-assets/fa-depreciation.png',
        ],
      },
      {
        icon: '📊',
        title: 'Reports',
        description: 'Register and depreciation reports for finance and audit.',
        images: [
          '/images/products/fixed-assets/fa-reports.png',
        ],
      },
    ],
    meta: {
      title: 'AKAS Fixed Assets | Asset register, classes and depreciation',
      description:
        'AKAS Fixed Assets manages asset classes, the asset register, periodic depreciation posting, and fixed asset reports.',
      keywords: 'fixed assets, depreciation, asset register, AKAS accounting',
    },
    additionalFeatures: [
      {
        icon: '📒',
        title: 'Posts to GL',
        description: 'Depreciation entries land in the same ledger as operational journals.',
      },
      {
        icon: '📍',
        title: 'Shared locations',
        description: 'Assets sit on Administration locations used by inventory.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'Register instead of a list',
        description: 'Each asset is a record with class, location, and history.',
      },
      {
        number: '02',
        title: 'Depreciation on a schedule',
        description: 'Periodic calculation posts the books; you review exceptions.',
      },
      {
        number: '03',
        title: 'Aligned with Accounting',
        description: 'No separate fixed-asset workbook to reconcile at close.',
      },
      {
        number: '04',
        title: 'Ready for audit questions',
        description: 'Class, register, and posted depreciation are in one module.',
      },
    ],
    related: [
      { label: 'Accounting', to: '/products/accounting' },
      { label: 'Expenses', to: '/products/expenses' },
      { label: 'Fixed assets user guide', href: `${DOCS_ORIGIN}/en/fixed-assets/` },
    ],
  },
  {
    id: 11,
    slug: 'online-store',
    name: 'Online Store',
    shortName: 'Online Store',
    navLabel: 'Online Store',
    tagline: 'A storefront on the same items, prices, and payments as the ERP',
    featured: true,
    docsUrl: `${DOCS_ORIGIN}/en/online-store/`,
    description:
      'The AKAS online store is the customer-facing catalog, live at yourbrand.akas.co. You configure store information, payment methods, banners, categories, terms and conditions, and social channels — then customize the storefront. Products, stock, and payments are the same records used in Revenues and Inventory, so a web order is not a second product database. Checkout supports Cash on Delivery and ABA PayWay; shipping policies appear when ecommerce is enabled.',
    images: [
      '/images/products/online-store/akas-store-info.png',
      '/images/products/online-store/akas-store-paymethod.png',
      '/images/products/online-store/akas-store-banner-list.png',
      '/images/products/online-store/akas-store-banner-form.png',
      '/images/products/online-store/akas-store-terms.png',
      '/images/products/online-store/akas-store-menu.png',
      '/images/products/online-store/biz-admin-orgchart.png',
      '/images/products/online-store/biz-admin-orgchart-create.png',
      '/images/products/online-store/biz-admin-orgchart-com.png',
    ],
    backgroundImage: '/images/products/online-store/akas-store-info.png',
    highlights: [
      'Storefront at yourbrand.akas.co with your branding and banners',
      'Checkout with ABA PayWay and Cash on Delivery',
      'Categories, banners, and editable terms and conditions',
      'Catalog and stock shared with the ERP',
    ],
    workflow: {
      title: 'Open the store',
      path: 'Store info → Payments → Banners & terms → Customize → Sell',
      steps: [
        {
          title: 'Store information',
          description: 'Name, contact, and storefront identity for your business.',
        },
        {
          title: 'Payment methods',
          description: 'Enable the methods customers can use at checkout.',
        },
        {
          title: 'Banners and terms',
          description: 'Publish campaigns and the terms and conditions that apply.',
        },
        {
          title: 'Customize and sell',
          description: 'Adjust the storefront, then take orders against the live item catalog.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Store setup',
        items: [
          'Store information with identity, slogan, and branding colors',
          'Store payment methods — ABA PayWay and Cash on Delivery',
          'Store banners and category "show in store" flags with shipping policies',
          'Merchant-editable terms and conditions with a sample generator',
          'Social channels: Telegram, WhatsApp, Facebook, Instagram, TikTok, LinkedIn, X, YouTube',
        ],
      },
      {
        title: 'Customization',
        items: ['Store customization for layout and branding'],
      },
    ],
    mainFeatures: [
      {
        icon: '🏪',
        title: 'Store identity',
        description:
          'Configure the public store: information, banners, and terms that customers see before they buy.',
        images: [
          '/images/products/online-store/akas-store-info.png',
          '/images/products/online-store/akas-store-banner-list.png',
          '/images/products/online-store/akas-store-banner-form.png',
          '/images/products/online-store/akas-store-terms.png',
          '/images/products/online-store/akas-store-menu.png',
        ],
      },
      {
        icon: '💳',
        title: 'Payments',
        description:
          'Checkout accepts card payments through ABA PayWay, cash on delivery, and the scan-to-pay (KHQR) methods you run in Revenues — so web orders land in the same books as every other sale.',
        images: [
          '/images/products/online-store/akas-store-paymethod.png',
        ],
      },
      {
        icon: '🎨',
        title: 'Customization',
        description:
          'Adjust the storefront — branding, banners, categories, and terms — without standing up a separate ecommerce stack.',
        images: [
          '/images/products/online-store/biz-admin-orgchart.png',
          '/images/products/online-store/biz-admin-orgchart-create.png',
          '/images/products/online-store/biz-admin-orgchart-com.png',
        ],
      },
      {
        icon: '📦',
        title: 'Shared catalog',
        description:
          'Items, prices, and availability come from Revenues and Inventory. Web, POS, and invoices stay aligned.',
      },
    ],
    meta: {
      title: 'AKAS Online Store | Ecommerce on the same catalog as the ERP',
      description:
        'Configure the AKAS online storefront: store information, payment methods, banners, terms, and customization, selling the same items as Revenues and Inventory.',
      keywords: 'ecommerce, online store, KHQR store, AKAS storefront',
    },
    additionalFeatures: [
      {
        icon: '🚚',
        title: 'Shipping policies',
        description: 'Available when ecommerce is enabled.',
      },
      {
        icon: '🖥️',
        title: 'Live demos',
        description: 'Try the front store and store admin from the AKAS demo page.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'One catalog',
        description: 'No sync job between “the website products” and “the ERP products.”',
      },
      {
        number: '02',
        title: 'Payments finance already knows',
        description: 'Methods are configured in the ERP, not only for the storefront theme.',
      },
      {
        number: '03',
        title: 'Campaigns without a rebuild',
        description: 'Banners and terms are content, not a developer ticket.',
      },
      {
        number: '04',
        title: 'Orders join quote-to-cash',
        description: 'Web sales sit with POS and invoices in Revenues.',
      },
    ],
    related: [
      { label: 'Revenues', to: '/products/revenues' },
      { label: 'Point of Sale', to: '/products/pos' },
      { label: 'Try the store demo', to: '/demo' },
      { label: 'Online store user guide', href: `${DOCS_ORIGIN}/en/online-store/` },
    ],
  },
  {
    id: 12,
    slug: 'platform',
    name: 'Platform & Administration',
    shortName: 'Platform',
    navLabel: 'Platform & Admin',
    tagline: 'Org chart, workflows, permissions, custom fields, and AI',
    featured: true,
    docsUrl: `${DOCS_ORIGIN}/en/admin/`,
    description:
      'System Administration is the control plane for every AKAS module. Set up legal entities and the org chart, locations, fiscal years, and currencies. Standardize auto-numbers, lists of values, credit terms, and message templates. Design visual approval workflows, add custom fields, manage translations, manage users, roles, permissions, and 2FA, and optionally enable the AI assistant. Data logs and system jobs keep an operational trail.',
    images: [
      '/images/products/platform/admin-areas.png',
      '/images/products/platform/admin-location-create.png',
      '/images/products/platform/admin-fiscal-years.png',
      '/images/products/platform/admin-fiscal-year-edit.png',
      '/images/products/platform/admin-autonumbers.png',
      '/images/products/platform/admin-lov.png',
      '/images/products/platform/akas-admin-msg-tpls.png',
      '/images/products/platform/akas-admin-msg-tpl-form.png',
      '/images/products/platform/akas-admin-settings.png',
      '/images/products/platform/admin-01-users-list.png',
      '/images/products/platform/admin-02-user-form.png',
      '/images/products/platform/admin-03-roles-list.png',
      '/images/products/platform/admin-04-role-form.png',
      '/images/products/platform/admin-05-role-permissions.png',
      '/images/products/platform/admin-06-grant-role.png',
      '/images/products/platform/2fa-auth.png',
      '/images/products/platform/2fa-auth-step2.png',
      '/images/products/platform/2fa-auth-step3.png',
      '/images/products/platform/admin-workflows.png',
      '/images/products/platform/admin-workflow-designer.png',
      '/images/products/platform/admin-workflow-form.png',
      '/images/products/platform/admin-workflow-requests.png',
      '/images/products/platform/admin-form-design-mode.png',
      '/images/products/platform/admin-custom-field-form.png',
      '/images/products/platform/admin-translations.png',
      '/images/products/platform/admin-translation-form.png',
      '/images/products/platform/admin-data-logs.png',
      '/images/products/platform/admin-data-log-detail.png',
      '/images/products/platform/admin-system-jobs.png',
      '/images/products/platform/admin-system-job-form.png',
      '/images/products/platform/admin-ai-assistant-chat.png',
      '/images/products/platform/admin-ai-model-settings.png',
      '/images/products/platform/admin-ai-assistant-fab.png',
      '/images/products/platform/admin-ai-assistant-floating.png',
      '/images/products/platform/admin-list-column-settings.png',
    ],
    backgroundImage: '/images/products/platform/admin-workflow-designer.png',
    highlights: [
      'Org chart, locations, fiscal years, currencies',
      'Visual workflow designer and workflow requests',
      'Users, roles, permissions, and two-factor authentication',
      'Custom fields, translations, auto-numbers, and AI assistant',
    ],
    workflow: {
      title: 'Stand up the system',
      path: 'Org & fiscal setup → Masters → Security → Workflows → Customize',
      steps: [
        {
          title: 'Structure the company',
          description: 'Org chart, logo, locations, fiscal years, currencies, and areas.',
        },
        {
          title: 'Standardize data',
          description: 'Auto-numbers, lists of values, credit terms, message templates, and system settings.',
        },
        {
          title: 'Secure access',
          description: 'Users, roles, permissions, and optional two-factor authentication.',
        },
        {
          title: 'Automate and tailor',
          description: 'Design workflows, add custom fields, translate UI, schedule jobs, and enable AI chat.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Structural setup',
        items: [
          'Org chart and company logo',
          'Locations for inventory and assets',
          'Fiscal years',
          'Currencies and exchange rates',
          'Geographical areas',
        ],
      },
      {
        title: 'Standardization',
        items: [
          'Auto-numbers for invoices, POs, employee IDs',
          'Lists of values (lead sources, industries, and more)',
          'Credit terms',
          'Message templates for email/SMS',
          'Translations (override wording or add languages)',
        ],
      },
      {
        title: 'Security and operations',
        items: [
          'Users, roles, and permissions',
          'Two-factor authentication',
          'Workflows and workflow requests',
          'Form layout and custom fields',
          'AI assistant',
          'System jobs and data logs',
        ],
      },
    ],
    mainFeatures: [
      {
        icon: '🏢',
        title: 'Org, locations, fiscal years',
        description:
          'Create legal entities and departments, map warehouses and stores, and define the accounting periods that gate posting.',
        images: [
          '/images/products/platform/admin-areas.png',
          '/images/products/platform/admin-location-create.png',
          '/images/products/platform/admin-fiscal-years.png',
          '/images/products/platform/admin-fiscal-year-edit.png',
        ],
      },
      {
        icon: '🔀',
        title: 'Visual workflows',
        description:
          'Draw the approval path for leave, quotes, bills, and stock: who receives a request, what checks apply, who signs off, and who gets notified. Reuse the same rules in other departments, and see at a glance where anything is waiting.',
        images: [
          '/images/products/platform/admin-workflows.png',
          '/images/products/platform/admin-workflow-designer.png',
          '/images/products/platform/admin-workflow-form.png',
          '/images/products/platform/admin-workflow-requests.png',
        ],
      },
      {
        icon: '🔒',
        title: 'Users, roles, permissions, 2FA',
        description:
          'Staff sign in with their own account — including Google or the company directory — and can add extra protection on sensitive accounts. Roles carry a complete permission set per page, from viewing and entering to approving and adjusting records, and menus appear based on role. Client companies can get portal accounts and look after their own documents.',
        images: [
          '/images/products/platform/admin-01-users-list.png',
          '/images/products/platform/admin-02-user-form.png',
          '/images/products/platform/admin-03-roles-list.png',
          '/images/products/platform/admin-04-role-form.png',
          '/images/products/platform/admin-05-role-permissions.png',
          '/images/products/platform/admin-06-grant-role.png',
          '/images/products/platform/2fa-auth.png',
          '/images/products/platform/2fa-auth-step2.png',
          '/images/products/platform/2fa-auth-step3.png',
        ],
      },
      {
        icon: '🧩',
        title: 'Custom fields and translations',
        description:
          'Point and click to rearrange forms and lists, add the fields your industry needs, and preview the mobile layout — no software vendor required. English and Khmer are included, with wording you can adjust and more languages when you need them.',
        images: [
          '/images/products/platform/admin-form-design-mode.png',
          '/images/products/platform/admin-custom-field-form.png',
          '/images/products/platform/admin-translations.png',
          '/images/products/platform/admin-translation-form.png',
        ],
      },
      {
        icon: '🤖',
        title: 'AI assistant',
        description:
          'Turn on a conversational assistant over your own business data — "my leave balance", "unpaid invoices" — using the AI service you choose, including running locally if you prefer to keep data in-house.',
        images: [
          '/images/products/platform/admin-ai-assistant-chat.png',
          '/images/products/platform/admin-ai-model-settings.png',
          '/images/products/platform/admin-ai-assistant-fab.png',
          '/images/products/platform/admin-ai-assistant-floating.png',
        ],
      },
      {
        icon: '📝',
        title: 'Jobs, logs, auto-numbers',
        description:
          'Invoices, receipts, purchases, and staff IDs follow your numbering scheme automatically. Sensitive-field changes record what changed and when, and can be exported for a compliance review. Scheduled jobs handle backups and notifications in the background.',
        images: [
          '/images/products/platform/admin-autonumbers.png',
          '/images/products/platform/admin-data-logs.png',
          '/images/products/platform/admin-data-log-detail.png',
          '/images/products/platform/admin-system-jobs.png',
          '/images/products/platform/admin-system-job-form.png',
          '/images/products/platform/admin-list-column-settings.png',
        ],
      },
    ],
    meta: {
      title: 'AKAS Platform | Workflows, permissions, custom fields and translations',
      description:
        'AKAS System Administration configures org structure, workflows, users and permissions, custom fields, translations, 2FA, AI assistant, and audit logs for every ERP module.',
      keywords:
        'ERP administration, approval workflow, custom fields, multilingual ERP, 2FA, AKAS platform',
    },
    additionalFeatures: [
      {
        icon: '🔔',
        title: 'Message templates',
        description: 'Format the emails and SMS the rest of the system sends.',
      },
      {
        icon: '📁',
        title: 'File settings',
        description: 'Allowed extensions and max upload size for your files.',
      },
      {
        icon: '▶️',
        title: 'Workflow walkthrough',
        description: 'Watch the designer in the product videos on HRMS and the homepage.',
      },
    ],
    benefits: [
      {
        number: '01',
        title: 'Configure, don’t fork',
        description: 'Workflows, fields, and translations absorb most “we work differently” requests.',
      },
      {
        number: '02',
        title: 'Access you can explain',
        description: 'Roles and permissions decide who sees payroll versus who posts journals.',
      },
      {
        number: '03',
        title: 'Your language in the UI',
        description: 'Translations are maintained in admin — English and Khmer included, more languages when you need them.',
      },
      {
        number: '04',
        title: 'A trail when something changes',
        description: 'Data logs, workflow requests, and auto-numbers support internal control.',
      },
    ],
    videos: [
      {
        title: 'Flexible approval workflow',
        youtubeId: 'Rt-Zk9H0rNg',
        description: 'Design approval workflow in AKAS.',
      },
      {
        title: 'Customization',
        youtubeId: 'RlB4MXPjYXs',
        description: 'Form layout and customization demo.',
      },
    ],
    related: [
      { label: 'HRMS', to: '/products/hrms' },
      { label: 'Accounting', to: '/products/accounting' },
      { label: 'Administration user guide', href: `${DOCS_ORIGIN}/en/admin/` },
    ],
  },
];

/**
 * Overlay the Khmer records onto the English list, falling back per slug so a
 * product missing a Khmer translation simply renders in English.
 */
function localizeProducts(list: Product[], locale?: Locale): Product[] {
  if (locale !== 'km') return list;
  const kmBySlug = new Map(ProductsKm.map((product) => [product.slug, product]));
  return list.map((product) => kmBySlug.get(product.slug) ?? product);
}

export function getProductBySlug(slug?: string, locale?: Locale): Product | undefined {
  if (!slug) return undefined;
  return localizeProducts(Products, locale).find((product) => product.slug === slug);
}

export function getNavProducts(locale?: Locale): Product[] {
  const navSlugs = new Set<string>(NAV_PRODUCT_SLUGS);
  return localizeProducts(Products, locale).filter((product) => navSlugs.has(product.slug));
}

export function getFeaturedProducts(locale?: Locale): Product[] {
  return localizeProducts(Products, locale).filter((product) => product.featured);
}
